import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { waLink } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CULTIVOS = ["Soya", "Sorgo", "Maíz", "Caña de azúcar", "Girasol", "Otro"];
const ZONAS = ["Cuatro Cañadas", "Pailón", "San Julián", "Okinawa", "Montero / Mineros", "Chiquitania", "Otra zona"];

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const inputCls =
  "w-full bg-white/[0.04] border border-white/15 rounded-sm px-4 py-4 text-base text-[#F6F7F5] placeholder:text-[#F6F7F5]/35 focus:outline-none focus:border-[#00A651] focus:shadow-[0_0_20px_rgba(0,166,81,0.2)] transition-all duration-300";

const Cotizar = () => {
  const [form, setForm] = useState({ cultivo: "", zona: "", hectareas: "", whatsapp: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.cultivo || !form.zona || !form.hectareas || !form.whatsapp) {
      toast.error("Completá los 4 campos para cotizar.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, {
        cultivo: form.cultivo,
        zona: form.zona,
        hectareas: Number(form.hectareas),
        whatsapp: form.whatsapp,
      });
      toast.success("Cotización registrada. Te abrimos WhatsApp para confirmar.");
      const msg = `Hola Fumiga, quiero cotizar aplicación para ${form.cultivo} en ${form.zona} (${form.hectareas} ha). Mi número: ${form.whatsapp}`;
      window.open(waLink(msg), "_blank", "noopener,noreferrer");
      setForm({ cultivo: "", zona: "", hectareas: "", whatsapp: "" });
    } catch {
      toast.error("No pudimos guardar la cotización. Probá de nuevo o escribinos directo por WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="cotizar" data-testid="cotizar-section" className="relative bg-[#151A1F] py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 hairlines" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-6 sm:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <motion.p {...reveal} className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#00A651]">
              Cotizador de campo
            </motion.p>
            <motion.h2
              {...reveal}
              transition={{ ...reveal.transition, delay: 0.1 }}
              className="font-display font-bold tracking-tight leading-[1.08] text-2xl sm:text-4xl lg:text-5xl mt-6"
            >
              Cuatro campos. Cero vueltas.
            </motion.h2>
            <motion.p
              {...reveal}
              transition={{ ...reveal.transition, delay: 0.15 }}
              className="mt-6 text-base text-[#F6F7F5]/65 leading-relaxed max-w-md"
            >
              Decinos cultivo, zona, hectáreas y tu WhatsApp. Guardamos tu pedido y te abrimos
              el chat con el mensaje ya escrito. Respuesta en menos de 15 minutos en horario de campo.
            </motion.p>
            <motion.p
              {...reveal}
              transition={{ ...reveal.transition, delay: 0.2 }}
              className="mt-8 font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#F6F7F5]/40"
            >
              El precio es por hectárea · Sin compromiso
            </motion.p>
          </div>

          <motion.form
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.15 }}
            onSubmit={submit}
            data-testid="quote-form"
            className="lg:col-span-7 border border-white/10 bg-white/[0.02] p-6 sm:p-10 grid grid-cols-1 sm:grid-cols-2 gap-5"
          >
            <label className="block">
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#F6F7F5]/50">01 · Cultivo</span>
              <select
                data-testid="quote-field-cultivo"
                value={form.cultivo}
                onChange={set("cultivo")}
                className={`${inputCls} mt-2 appearance-none`}
                required
              >
                <option value="" disabled className="bg-[#151A1F]">Elegí el cultivo</option>
                {CULTIVOS.map((c) => (
                  <option key={c} value={c} className="bg-[#151A1F]">{c}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#F6F7F5]/50">02 · Zona</span>
              <select
                data-testid="quote-field-zona"
                value={form.zona}
                onChange={set("zona")}
                className={`${inputCls} mt-2 appearance-none`}
                required
              >
                <option value="" disabled className="bg-[#151A1F]">Elegí la zona</option>
                {ZONAS.map((z) => (
                  <option key={z} value={z} className="bg-[#151A1F]">{z}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#F6F7F5]/50">03 · Hectáreas</span>
              <input
                data-testid="quote-field-hectareas"
                type="number"
                min="1"
                step="any"
                value={form.hectareas}
                onChange={set("hectareas")}
                placeholder="Ej. 250"
                className={`${inputCls} mt-2 tabular`}
                required
              />
            </label>
            <label className="block">
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#F6F7F5]/50">04 · Tu WhatsApp</span>
              <input
                data-testid="quote-field-whatsapp"
                type="tel"
                value={form.whatsapp}
                onChange={set("whatsapp")}
                placeholder="+591 70000000"
                className={`${inputCls} mt-2 tabular`}
                required
              />
            </label>
            <div className="sm:col-span-2 mt-2">
              <button
                data-testid="quote-form-submit-button"
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto bg-[#FFB000] text-[#151A1F] font-display font-semibold text-base px-10 py-4 rounded-full hover:scale-[1.02] transition-transform duration-300 disabled:opacity-60 disabled:hover:scale-100 inline-flex items-center justify-center gap-3"
              >
                {loading && <Loader2 size={18} className="animate-spin" />}
                {loading ? "Enviando..." : "Calcular y enviar por WhatsApp"}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Cotizar;
